import { RefObject, useEffect, useState } from "react"

import { NO_SCROLLBAR_GUTTER, ScrollbarGutter, measureScrollbarGutter } from "@/lib/scrollbarGutter"

/**
 * Tracks the space a scroll container's own scrollbars take out of its visible box, so a pane
 * scrolled in lockstep with it can reserve the same room and keep the two in line all the way to
 * the end of the scroll. See {@link @/lib/scrollbarGutter} for why that drift happens.
 *
 * Re-measures on resize, which also covers a scrollbar appearing or disappearing as content grows
 * past the container: either changes the content box being observed.
 *
 * @param ref the element that actually scrolls — the one carrying the scrollbars
 */
export function useScrollbarGutter(ref: RefObject<HTMLElement | null>): ScrollbarGutter {
  const [gutter, setGutter] = useState<ScrollbarGutter>(NO_SCROLLBAR_GUTTER)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const measure = () => {
      const next = measureScrollbarGutter(element)
      // Same numbers must return the same object: this feeds a style prop, and a fresh object on
      // every resize tick would re-render both panes mid-scroll for nothing.
      setGutter((current) =>
        current.bottom === next.bottom && current.right === next.right ? current : next
      )
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])

  return gutter
}
